import { IsNotEmpty, IsOptional, IsString, Length } from 'class-validator';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Form from 'src/components/FormComponents/Form';
import { PhoneIcon, EnvelopeIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import container from 'src/container';
import { Organization } from 'src/domain/organization';
import { UserThunks } from 'src/state/thunks';
import { LoginContactFields } from 'src/components/AccountAccess/LoginContactFields';
import Input from 'src/components/FormComponents/Input';
import OrganizationAvatarInput, {
    HandleUploadOrganizationAvatar,
} from 'src/components/FormComponents/OrganizationAvatarInput';
import i18n from 'src/i18n';
import { LanguageSelect } from 'src/components/LanguageSelect/LanguageSelect';

const {
    cradle: { munduClient },
} = container;

const __schemaImports = {
    IsNotEmpty,
    IsOptional,
    IsString,
    Length,
    i18n,
};
void __schemaImports;

class EditOrganizationSchema {
    @IsOptional()
    @IsString()
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName?: string;

    @IsOptional()
    @IsString()
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName?: string;
}

export const OrganizationProfile = ({
    user,
    readOnly = false,
}: {
    user: Organization;
    readOnly?: boolean;
}) => {
    const dispatch = useDispatch();
    const notify = useNotification();
    const { t } = useTranslation();
    const avatarInputRef = useRef<HandleUploadOrganizationAvatar>(null);
    const [isAvatarDirty, setIsAvatarDirty] = useState(false);

    const handleUpdate = async (data: EditOrganizationSchema) => {
        try {
            await munduClient.updateOganization(user.id, data);
            await avatarInputRef.current?.uploadAvatar();
            setIsAvatarDirty(false);
            dispatch(UserThunks.refreshUser());
            notify(t('notifications.profileUpdated'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto">
            <Form
                className="bg-lightPeach text-woodyBrown"
                schema={EditOrganizationSchema}
                onSubmit={handleUpdate}
                mode="onChange"
                defaultValues={{
                    firstName: user.profile.firstName,
                    lastName: user.profile.lastName,
                }}
            >
                {({
                    register,
                    formState: { isValid, errors, isSubmitting, isDirty },
                }) => (
                    <div className="grid grid-cols-2 gap-4 h-full">
                        <div className="col-span-full flex justify-center">
                            <OrganizationAvatarInput
                                ref={avatarInputRef}
                                organization={user}
                                readOnly={readOnly}
                                kind="superuser"
                                onAvatarDirtyChange={setIsAvatarDirty}
                            />
                        </div>
                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.firstName')}
                            error={errors.firstName}
                            readOnly={readOnly}
                            {...register('firstName')}
                        />
                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.lastName')}
                            error={errors.lastName}
                            readOnly={readOnly}
                            {...register('lastName')}
                        />
                        <LoginContactFields
                            userId={user.id}
                            readOnly={readOnly}
                        />
                        {!readOnly ? (
                            <div className="flex items-center gap-6">
                                <Input
                                    value={t('common.save')}
                                    className="primary-btn-small"
                                    disabled={
                                        !isValid ||
                                        isSubmitting ||
                                        (!isDirty && !isAvatarDirty)
                                    }
                                    type="submit"
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </Form>

            <Section title={t('profile.language')}>
                <div className="items-start">
                    <LanguageSelect />
                </div>
            </Section>
        </div>
    );
};
